import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom } from 'rxjs';

import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';

import { environment } from '../../../../../environments/environment';
import { AlertService } from '../alert/alert.service';

interface PdfResponse {
  pdfBase64: string;
}

/**
 * Requests the server-rendered graphical representation (PDF) of an electronic
 * Documento Soporte (DS) or Nota de Ajuste al DS (NAS) from the main API and
 * opens it in a new tab. The API owns the mapping + pdfmake rendering; this
 * service only triggers it and displays the result.
 */
@Injectable({
  providedIn: 'root',
})
export class ElectronicDocumentPdfService {
  private http = inject(HttpClient);
  private cookieAuthService = inject(CookieAuthService);
  private spinnerService = inject(NgxSpinnerService);
  private alertService = inject(AlertService);
  private translate = inject(TranslateService);

  private readonly apiUrl = environment.api_url;

  private getCompanyId(): string {
    return this.cookieAuthService.getCompanyObject!.Id_company!.toString();
  }

  async openAdjustmentNotePdf(refundId: number): Promise<void> {
    await this.requestAndOpen('/pdf/adjustment-note', {
      companyId: this.getCompanyId(),
      refundId,
    });
  }

  async openSupportDocumentPdf(purchaseId: number): Promise<void> {
    await this.requestAndOpen('/pdf/support-document', {
      companyId: this.getCompanyId(),
      purchaseId,
    });
  }

  private async requestAndOpen(
    path: string,
    body: Record<string, unknown>,
  ): Promise<void> {
    try {
      this.spinnerService.show();
      const response = await firstValueFrom(
        this.http.post<PdfResponse>(`${this.apiUrl}${path}`, body),
      );
      this.openBase64Pdf(response.pdfBase64);
    } catch (error) {
      this.alertService.open({
        title: this.translate.instant('global.alerts.warning'),
        message: this.translate.instant('global.pdf.generate-error'),
        type: 'error',
      });
      return Promise.reject(error);
    } finally {
      this.spinnerService.hide();
    }
  }

  private openBase64Pdf(base64: string): void {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }
}
